import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import FileSpreadsheet from 'lucide-react/dist/esm/icons/file-spreadsheet';
import { useAmlStore } from '../../stores/amlStore';
import { TableEmptyRow, NUM_FMT, downloadBlob } from '../shared';
import { amlApi } from '../../services/amlApi';
import { DatePicker } from '../../../../components/ui';
import { Button, Spinner, Tooltip } from '../../../../components/ui';
import type { AmlPivotRow } from '../../types/aml';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';

const getRowStyles = (level: string): string => {
  switch (level) {
    case 'year':
      return 'bg-surface-secondary/70 font-bold';
    case 'quarter':
      return 'bg-brand/[0.08] font-semibold';
    case 'month':
      return 'bg-surface-secondary/30';
    case 'grand_total':
      return 'bg-brand/[0.12] font-bold';
    case 'customer':
    default:
      return 'hover:bg-surface-secondary transition-colors';
  }
};

const TransactionPivotReport: React.FC = () => {
  const {
    pivotRows,
    pivotFilters,
    loading,
    fetchPivotReport,
    setPivotFilters,
  } = useAmlStore(
    useShallow((s) => ({
      pivotRows: s.pivotRows,
      pivotFilters: s.pivotFilters,
      loading: s.loading,
      fetchPivotReport: s.fetchPivotReport,
      setPivotFilters: s.setPivotFilters,
    })),
  );

  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);

  const [downloading, setDownloading] = useState<'pdf' | 'excel' | null>(null);

  const byClient = pivotFilters.by_client ?? 'yes';
  const isByClient = byClient === 'yes';

  const hasFilters = !!(pivotFilters.date_from || pivotFilters.date_to);

  useEffect(() => { fetchPivotReport(); }, [fetchPivotReport]);

  const handleApply = useCallback(() => {
    fetchPivotReport();
  }, [fetchPivotReport]);

  const handleReset = useCallback(() => {
    setPivotFilters({ by_client: 'yes' });
    fetchPivotReport();
  }, [setPivotFilters, fetchPivotReport]);

  const handleDownloadPdf = useCallback(async () => {
    setDownloading('pdf');
    try {
      const blob = await amlApi.downloadPivotPdf({
        date_from: pivotFilters.date_from || undefined,
        date_to: pivotFilters.date_to || undefined,
        by_client: pivotFilters.by_client || 'yes',
      });
      downloadBlob(blob as Blob, 'transaction-pivot-report.pdf');
    } catch (e) {
      console.error('Failed to download PDF', e);
    } finally {
      setDownloading(null);
    }
  }, [pivotFilters]);

  const handleDownloadExcel = useCallback(async () => {
    setDownloading('excel');
    try {
      const blob = await amlApi.downloadPivotExcel({
        date_from: pivotFilters.date_from || undefined,
        date_to: pivotFilters.date_to || undefined,
        by_client: pivotFilters.by_client || 'yes',
      });
      downloadBlob(blob as Blob, 'pivot-report.xlsx');
    } catch (e) {
      console.error('Failed to download Excel', e);
    } finally {
      setDownloading(null);
    }
  }, [pivotFilters]);

  const handleSetByClient = useCallback(
    () => setPivotFilters({ ...pivotFilters, by_client: 'yes' }),
    [setPivotFilters, pivotFilters],
  );
  const handleSetByMonth = useCallback(
    () => setPivotFilters({ ...pivotFilters, by_client: 'no' }),
    [setPivotFilters, pivotFilters],
  );
  const handleDateFrom = useCallback(
    (v: string) => setPivotFilters({ ...pivotFilters, date_from: v || undefined }),
    [setPivotFilters, pivotFilters],
  );
  const handleDateTo = useCallback(
    (v: string) => setPivotFilters({ ...pivotFilters, date_to: v || undefined }),
    [setPivotFilters, pivotFilters],
  );

  const visibleRows = useMemo(() => {
    if (isByClient) return pivotRows;
    return pivotRows.filter((r) => r.level !== 'customer');
  }, [pivotRows, isByClient]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 px-1">
            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-content">
              <input
                type="radio"
                name="pivotByClient"
                checked={isByClient}
                onChange={handleSetByClient}
                className="w-3.5 h-3.5 accent-brand"
              />
              {tr('amlPivotRadioClient')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-content">
              <input
                type="radio"
                name="pivotByClient"
                checked={!isByClient}
                onChange={handleSetByMonth}
                className="w-3.5 h-3.5 accent-brand"
              />
              {tr('amlPivotRadioMonth')}
            </label>
          </div>

          <DatePicker
            value={pivotFilters.date_from ?? ''}
            onChange={handleDateFrom}
            placeholder={tr('amlPlaceholderStartDate')}
            size="sm"
            className="w-44"
          />
          <DatePicker
            value={pivotFilters.date_to ?? ''}
            onChange={handleDateTo}
            placeholder={tr('amlPlaceholderEndDate')}
            size="sm"
            className="w-44"
          />

          <Button variant="secondary" size="md" onClick={handleReset} disabled={!hasFilters}>
            {tr('amlBtnReset')}
          </Button>
          <Button variant="secondary" size="md" onClick={handleApply} disabled={!hasFilters}>
            {tr('amlBtnApply')}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="md"
            loading={downloading === 'pdf'}
            disabled={visibleRows.length === 0 || (downloading !== null && downloading !== 'pdf')}
            leftIcon={<FileText size={14} />}
            onClick={handleDownloadPdf}
          >
            {tr('amlBtnPdf')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            loading={downloading === 'excel'}
            disabled={visibleRows.length === 0 || (downloading !== null && downloading !== 'excel')}
            leftIcon={<FileSpreadsheet size={14} />}
            onClick={handleDownloadExcel}
          >
            {tr('amlBtnExcel')}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-content-muted">
            <Spinner size="md" />
          </div>
        ) : visibleRows.length === 0 ? (
          <TableEmptyRow title={tr('amlPivotTableNoPivotData')} description={tr('amlPivotTableNoPivotDataDesc')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: '1000px', width: '100%' }}>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
              </colgroup>
              <thead>
                <tr className="border-b border-subtle bg-surface-secondary/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColTransactionPeriod')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColInboundSum')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColInboundCount')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColOutboundSum')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColOutboundCount')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColInvoiceSum')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColInvoiceCount')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColStopsSum')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{tr('amlPivotColPcsCount')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row: AmlPivotRow, idx: number) => (
                    <tr
                      key={`${row.level}-${row.label}-${idx}`}
                      className={`border-b border-subtle ${getRowStyles(row.level)}`}
                    >
                      <td className="px-4 py-3 text-sm text-content overflow-hidden max-w-0">
                        <Tooltip content={row.label} placement="top" onlyWhenTruncated>
                          <span className="block truncate">{row.label}</span>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary whitespace-nowrap">{NUM_FMT.format(row.inbound_sum)}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary">{row.inbound_count}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary whitespace-nowrap">{NUM_FMT.format(row.outbound_sum)}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary">{row.outbound_count}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary whitespace-nowrap">{NUM_FMT.format(row.invoice_amount_sum)}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary">{row.invoice_amount_count}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary">{row.stops_sum}</td>
                      <td className="px-4 py-3 text-sm text-right text-content-secondary">{row.pcs_sum}</td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default TransactionPivotReport;
